import { useEffect } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Copy } from '@carbon/react/icons';
import {
  EventsPanel,
  EventsPanelOpenButton,
} from 'components/events-panel/events-panel';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import { ScenarioFormLayout } from 'components/scenario-form-layout';
import type { ScenarioNavItem } from 'components/scenario-nav/types';
import type { ScenarioMapFeature } from 'components/scenario-map/types';
import { ScenarioGlobalConfigs } from 'components/scenario-global-configs';
import { ScenarioAreaConfigs } from 'components/scenario-area-configs';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import type { AreaSelection, DifferentialScenarioFormData } from './types';
import { ScenarioFormProvider, useScenarioForm } from './contexts';
import {
  areaGeometriesAtom,
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  enumsLoadableAtom,
  cloneScenarioLoadableAtom,
  toastAtom,
  differentialMapConfigAtom,
} from './atoms';
import { DifferentialItemsTable } from './differential-items-table';
import {
  GLOBAL_SELECTION_ID,
  getAreaDisplayName,
  getAreaBadge,
  type FormMode,
} from './utils';
import styles from './scenario-form.module.scss';

interface ScenarioFormProps {
  initialValues: DifferentialScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}

/**
 * Main form component for creating/editing differential scenarios.
 * Wraps content in ScenarioFormProvider and renders the full layout.
 * @param {ScenarioFormProps} props - The form props
 * @return {JSX.Element} The scenario form component
 */
export function ScenarioForm({
  initialValues,
  mode,
  scenarioId,
  commentsUrl,
  eventsUrl,
}: ScenarioFormProps): JSX.Element {
  const geometries = useAtomValue(areaGeometriesAtom);

  return (
    <ScenarioFormProvider
      initialValues={initialValues}
      mode={mode}
      scenarioId={scenarioId}
      geometries={geometries}
    >
      <ScenarioFormContent
        scenarioId={scenarioId}
        commentsUrl={commentsUrl}
        eventsUrl={eventsUrl}
      />
    </ScenarioFormProvider>
  );
}

/**
 * Inner content component that consumes the form context and assembles the
 * shared ScenarioFormLayout with differential-specific content panels.
 * @return {JSX.Element} The form content layout
 */
function ScenarioFormContent({
  scenarioId,
  commentsUrl,
  eventsUrl,
}: {
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}): JSX.Element {
  const {
    form,
    mode,
    selectedId,
    setSelectedId,
    addArea,
    removeArea,
    handleSave,
    isSaving,
  } = useScenarioForm();

  const geometries = useAtomValue(areaGeometriesAtom);
  const [toast, setToast] = useAtom(toastAtom);
  const [cloneState, triggerClone] = useAtom(cloneScenarioLoadableAtom);
  const history = useHistory();

  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);
  const enumsState = useAtomValue(enumsLoadableAtom);
  const isCloning = cloneState.state === 'loading';

  useEffect(() => {
    if (categoriesState.state === 'hasError') {
      setToast({ message: 'Failed to load area categories', type: 'error' });
    }
  }, [categoriesState.state, setToast]);

  useEffect(() => {
    if (unitTypesState.state === 'hasError') {
      setToast({ message: 'Failed to load unit types', type: 'error' });
    }
  }, [unitTypesState.state, setToast]);

  useEffect(() => {
    if (enumsState.state === 'hasError') {
      setToast({ message: 'Failed to load enum values', type: 'error' });
    }
  }, [enumsState.state, setToast]);

  useEffect(() => {
    if (Object.keys(geometries).length > 0) {
      form.validate('change');
    }
  }, [geometries, form]);

  const handleClone = () => {
    if (!scenarioId) return;
    triggerClone(Number(scenarioId))
      .then((data) => {
        history.push(data.href);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    history.push(routes.differentialScenariosPath);
  };

  const scenarioName = form.getFieldValue('global.name');

  return (
    <form.Subscribe
      selector={(state) => {
        const { areas } = state.values;
        const overlapping = getMemoizedOverlappingAreaIds(areas, geometries);

        // Still rebuilt on every store emission — every keystroke re-renders
        // the whole nav and map. Only the overlap scan above is memoized so
        // far; stabilising this selector on the four sibling scenario forms is
        // INS-12063 (it needs the object-literal return dealt with too, since
        // `form.Subscribe` compares the whole result with `===`). Not an
        // oversight, just not this ticket.
        const navItems: ScenarioNavItem[] = areas.map(
          (area: AreaSelection) => ({
            id: area.id,
            name: getAreaDisplayName(area),
            badge: getAreaBadge(area),
            hasOverlap: overlapping.has(area.id),
            isNew: area.status === 'new',
          })
        );

        const mapFeatures: ScenarioMapFeature[] = areas
          .filter((a: AreaSelection) => geometries[a.id])
          .map((a: AreaSelection) => ({
            id: a.id,
            geometry: geometries[a.id],
            type:
              a.geometryType === 'slot'
                ? ('well-stick' as const)
                : ('polygon' as const),
          }));

        const currentArea =
          selectedId !== GLOBAL_SELECTION_ID
            ? areas.find((a: AreaSelection) => a.id === selectedId)
            : undefined;

        return {
          navItems,
          mapFeatures,
          notesValue: state.values.global.notes,
          contentTitle: currentArea
            ? getAreaDisplayName(currentArea)
            : 'Scenario Details',
          canSubmit: state.canSubmit,
          isDirty: state.isDirty,
          hasOverlap: overlapping.size > 0,
          selectedAreaOverlap:
            currentArea && overlapping.has(currentArea.id)
              ? 'Overlaps with an existing area'
              : undefined,
        };
      }}
    >
      {({
        navItems,
        mapFeatures,
        notesValue,
        contentTitle,
        canSubmit,
        isDirty,
        hasOverlap,
        selectedAreaOverlap,
      }) => {
        const canSave =
          (mode === 'create' ? canSubmit : canSubmit && isDirty) && !hasOverlap;

        return (
          <ScenarioFormLayout
            title={
              mode === 'create'
                ? 'New Differential Scenario'
                : scenarioName || 'Edit Differential Scenario'
            }
            notes={{
              value: notesValue,
              onChange: (v) => form.setFieldValue('global.notes', v),
              placeholder: 'Click to add notes...',
            }}
            mapFeatures={mapFeatures}
            mapFocusedId={typeof selectedId === 'number' ? selectedId : null}
            mapConfigAtom={differentialMapConfigAtom}
            navItems={navItems}
            navSelectedId={selectedId}
            onNavSelect={setSelectedId}
            onNavAdd={addArea}
            onNavDelete={removeArea}
            navGlobalLabel="Scenario Details"
            contentTitle={contentTitle}
            overlapError={selectedAreaOverlap}
            isDirty={isDirty}
            canSave={canSave}
            isSaving={isSaving}
            onSave={handleSave}
            saveLabel="Save Scenario"
            headerActions={
              <div className={styles.headerActions}>
                {mode === 'edit' && (
                  <>
                    <EventsPanelOpenButton />
                    <Button
                      kind="tertiary"
                      size="md"
                      renderIcon={Copy}
                      disabled={isCloning}
                      onClick={handleClone}
                    >
                      {isCloning ? <InlineLoading /> : 'Clone Scenario'}
                    </Button>
                  </>
                )}
                <Button kind="secondary" size="md" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            }
            toast={toast}
            onToastClose={() => setToast(null)}
            eventsPanel={
              mode === 'edit' && commentsUrl && eventsUrl ? (
                <EventsPanel
                  header={scenarioName || ''}
                  title="Differential Scenario"
                  helperText="Share information about this scenario."
                  commentsHref={commentsUrl}
                  eventsHref={eventsUrl}
                />
              ) : undefined
            }
          >
            {selectedId === GLOBAL_SELECTION_ID ? (
              <ScenarioGlobalConfigs form={form} />
            ) : (
              <form.Subscribe
                selector={(state) =>
                  state.values.areas.findIndex(
                    (a: AreaSelection) => a.id === selectedId
                  )
                }
              >
                {(areaIndex) => {
                  if (areaIndex < 0) return null;
                  return (
                    <div className={styles.areaSections}>
                      <ScenarioAreaConfigs
                        form={form}
                        areaIndex={areaIndex}
                        enabledTypes={['area', 'unit', 'slot']}
                        notesPlaceholder="Notes about this area's differential overrides..."
                        collapsible
                      />
                      <div className={styles.sectionDivider}>
                        <DifferentialItemsTable areaIndex={areaIndex} />
                      </div>
                    </div>
                  );
                }}
              </form.Subscribe>
            )}
          </ScenarioFormLayout>
        );
      }}
    </form.Subscribe>
  );
}
